'use client';

import CircleCheckSvg from '@/assets/svgs/circle-check.svg';
import CircleExclamationOutlineSvg from '@/assets/svgs/circle-exclamation-outline.svg';
import CircleTimesSvg from '@/assets/svgs/circle-times.svg';
import ImageSvg from '@/assets/svgs/image.svg';
import UploadIconSvg from '@/assets/svgs/text-editor/upload.svg';
import TrashCanSvg from '@/assets/svgs/trash-can.svg';
import UserTypeIconSvg from '@/assets/svgs/user-type.svg';
import VideoSvg from '@/assets/svgs/video.svg';
import { useBuildVideoEnhancements, useUploadMedia } from '@/containers/MediaManager/api';
import { selectTotalCount } from '@/containers/MediaManager/redux/mediaManager.selectors';
import {
  addMedia,
  setTotalCount,
} from '@/containers/MediaManager/redux/mediaManager.slice';
import {
  Caption,
  FileUploadItem,
  Thumbnail,
  UploadMediaFormData,
  VideoEnhancement,
} from '@/containers/MediaManager/types';
import { isVideoFile, validateFile } from '@/containers/MediaManager/utils/fileHelpers';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { selectAccountIdentities } from '@/lib/redux/app.selectors';
import { useAppDispatch } from '@/lib/redux/store';
import { DetailsTabs, TabItem } from '@/shared/components/DetailsTabs';
import { IdentitySelector } from '@/shared/components/IdentitySelector';
import InfoAlert from '@/shared/components/InfoAlert/InfoAlert';

import { AxiosProgressEvent } from 'axios';
import classNames from 'classnames';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modals } from '../all-modals';
import ModalBody from '../ModalBody';
import ModalContainer from '../ModalContainer';
import ModalFooter from '../ModalFooter';
import ModalHeader from '../ModalHeader';
import { MediaUploadModalParams, ModalProps } from '../types';
import useModals from '../useModals';

const modalName = Modals.MediaUploadModal;

// Helper function to format caption timestamps
const formatCaptionTime = (time: string): string => {
  // Format: "00:02,100" -> "00:02.1"
  // Replace comma with period and take only first digit of milliseconds
  return time.replace(/,(\d)\d*/, '.$1');
};

interface MediaUploadModalProps extends ModalProps {
  className?: string;
  params?: MediaUploadModalParams;
}

const MediaUploadModal = ({ className, params }: MediaUploadModalProps) => {
  const { isModalOpen, onClose } = useModals({
    modalName,
  });

  // Extract params
  const accountId = params?.accountId || '';

  // Get available identities
  const accountIdentities = useSelector(selectAccountIdentities);
  const availableIdentities = React.useMemo(
    () => params?.identities || accountIdentities || [],
    [params?.identities, accountIdentities]
  );

  // Hooks
  const dispatch = useAppDispatch();
  const { showError, showSuccess } = useToast();
  const uploadMutation = useUploadMedia();
  const buildEnhancementsMutation = useBuildVideoEnhancements();
  const currentTotalCount = useSelector(selectTotalCount);

  // Step management
  const [step, setStep] = useState<'upload' | 'enhance'>('upload');
  const [files, setFiles] = useState<FileUploadItem[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [formIndex, setFormIndex] = useState(0);

  // Enhancement step state
  const [videosToEnhance, setVideosToEnhance] = useState<VideoEnhancement[]>([]);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [selectedThumbnail, setSelectedThumbnail] = useState<Thumbnail | null>(null);
  const [noThumbnailSelected, setNoThumbnailSelected] = useState(false);
  const [activeTab, setActiveTab] = useState<'thumbnails' | 'captions'>('thumbnails');
  const [enhancementInProgress, setEnhancementInProgress] = useState(false);
  const [enhancementProgress, setEnhancementProgress] = useState(0);
  const [hasSubtitles, setHasSubtitles] = useState(false);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [captions, setCaptions] = useState<Caption[]>([]);

  // Progress tracking
  const progressIntervalsRef = useRef<Record<number, NodeJS.Timeout>>({});
  const [selectedIdentityId, setSelectedIdentityId] = useState<string>(
    availableIdentities.length === 1 ? availableIdentities[0].identity_id : ''
  );

  // Auto-select identity if only one available
  useEffect(() => {
    if (availableIdentities.length === 1) {
      setSelectedIdentityId(availableIdentities[0].identity_id);
    }
  }, [availableIdentities]);

  // Update all files when identity changes
  useEffect(() => {
    if (selectedIdentityId) {
      setFiles((prev) => prev.map((f) => ({ ...f, identityId: selectedIdentityId })));
    }
  }, [selectedIdentityId]);

  const startEnhancement = useCallback(() => {
    if (videosToEnhance.length === 0) return;

    setStep('enhance');
    setNoThumbnailSelected(true);
    setActiveTab('thumbnails');

    // Initialize with first video
    const firstVideo = videosToEnhance[0];
    setHasSubtitles(!!firstVideo.subtitles && firstVideo.subtitles.length > 0);
    setCaptions(firstVideo.subtitles || []);
    setSubtitlesEnabled(false);
  }, [videosToEnhance]);

  // Handle enhancement transition when videos are ready
  useEffect(() => {
    // Only check after upload is complete and not already in enhancement step
    if (isUploading || step === 'enhance') return;

    // Check if all files are done processing
    const allDone =
      files.length > 0 &&
      files.every((f) => f.uploadStatus === 'success' || f.uploadStatus === 'error');

    if (!allDone) return;

    // Determine next action based on videos to enhance
    if (videosToEnhance.length === 1) {
      // Auto-start enhancement for single video
      startEnhancement();
    } else if (videosToEnhance.length === 0) {
      // No videos to enhance - check if we should close
      const hasErrors = files.some((f) => f.uploadStatus === 'error');
      if (!hasErrors) {
        // All successful and no videos to enhance - close modal
        onClose();
      }
    }
    // else: Multiple videos to enhance - show "Enhance Videos" button (handled in render)
  }, [videosToEnhance.length, isUploading, files, startEnhancement, onClose, step]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setFiles([]);
      setIsDragOver(false);
      setIsUploading(false);
      setStep('upload');
      setVideosToEnhance([]);
      setCurrentVideoIndex(0);
      setSelectedThumbnail(null);
      setNoThumbnailSelected(false);
      setActiveTab('thumbnails');
      setEnhancementInProgress(false);
      setEnhancementProgress(0);
      setHasSubtitles(false);
      setSubtitlesEnabled(false);
      setCaptions([]);
      setFormIndex(0);
      // Clear all progress intervals
      Object.values(progressIntervalsRef.current).forEach(clearInterval);
      progressIntervalsRef.current = {};
    }
  }, [isModalOpen]);

  const handleFileSelect = useCallback(
    (fileList: FileList | File[]) => {
      const fileArray = Array.from(fileList);
      const validFiles: FileUploadItem[] = [];
      const errors: string[] = [];

      fileArray.forEach((file) => {
        const error = validateFile(file);
        if (error) {
          errors.push(error);
        } else {
          validFiles.push({
            file,
            title: file.name,
            identityId: selectedIdentityId,
            formIndex: formIndex + validFiles.length,
            uploadStatus: 'idle',
            uploadProgress: 0,
            wantsEnhancement: false,
          });
        }
      });

      if (errors.length > 0) {
        showError(`Upload validation failed:\n${errors.join('\n')}`);
      }

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
        setFormIndex((prev) => prev + validFiles.length);
      }
    },
    [formIndex, selectedIdentityId, showError]
  );

  const handleFileRemove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const droppedFiles = e.dataTransfer.files;
      if (droppedFiles.length > 0) {
        handleFileSelect(droppedFiles);
      }
    },
    [handleFileSelect]
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        handleFileSelect(selectedFiles);
      }
      // Reset input so the same file can be selected again
      e.target.value = '';
    },
    [handleFileSelect]
  );

  const handleEnhancementCheckChange = useCallback((index: number, checked: boolean) => {
    setFiles((prev) =>
      prev.map((f, i) => (i === index ? { ...f, wantsEnhancement: checked } : f))
    );
  }, []);

  // Progress tracking with 50/50 formula
  const trackUploadProgress = useCallback(
    (fileItem: FileUploadItem, progressEvent: AxiosProgressEvent) => {
      const { loaded, total } = progressEvent;
      if (!total) return;

      const realProgress = (loaded / total) * 50;

      setFiles((prev) =>
        prev.map((f) =>
          f.formIndex === fileItem.formIndex
            ? {
                ...f,
                uploadProgress: Math.min(realProgress, 50),
                uploadStartTime: f.uploadStartTime || Date.now(),
              }
            : f
        )
      );

      // When upload reaches 50%, start estimated progress
      if (realProgress >= 49) {
        setFiles((prev) => {
          const currentFile = prev.find((f) => f.formIndex === fileItem.formIndex);
          if (!currentFile?.uploadStartTime) return prev;

          const uploadTotalTimeMs = Date.now() - currentFile.uploadStartTime;

          // Clear any existing interval
          if (progressIntervalsRef.current[fileItem.formIndex]) {
            clearInterval(progressIntervalsRef.current[fileItem.formIndex]);
          }

          // Start interval for estimated progress
          progressIntervalsRef.current[fileItem.formIndex] = setInterval(() => {
            setFiles((prevFiles) =>
              prevFiles.map((f) => {
                if (
                  f.formIndex === fileItem.formIndex &&
                  f.uploadStartTime &&
                  f.uploadStatus === 'uploading'
                ) {
                  const elapsed = Date.now() - f.uploadStartTime;
                  const estimatedProgress =
                    50 +
                    ((elapsed - uploadTotalTimeMs) / (uploadTotalTimeMs * 2 + 135000)) *
                      50;
                  return {
                    ...f,
                    uploadProgress: Math.min(estimatedProgress, 99),
                    uploadTotalTimeMs,
                  };
                }
                return f;
              })
            );
          }, 1000);

          return prev.map((f) =>
            f.formIndex === fileItem.formIndex ? { ...f, uploadTotalTimeMs } : f
          );
        });
      }
    },
    []
  );

  const uploadSingleFile = useCallback(
    async (fileItem: FileUploadItem): Promise<boolean> => {
      try {
        // Update to uploading status
        setFiles((prev) =>
          prev.map((f) =>
            f.formIndex === fileItem.formIndex
              ? {
                  ...f,
                  uploadStatus: 'uploading',
                  uploadProgress: 0,
                  uploadStartTime: Date.now(),
                }
              : f
          )
        );

        // Create FormData
        const formData = new FormData() as UploadMediaFormData;
        formData.append('account_id', accountId);
        formData.append('identity_id', fileItem.identityId);
        formData.append('title', fileItem.title);
        formData.append('initial_file_upload', fileItem.file);

        // Determine which API to call
        const isVideo = isVideoFile(fileItem.file);
        const shouldEnhance = isVideo && fileItem.wantsEnhancement;

        if (shouldEnhance) {
          // Call buildVideoEnhancements API
          const response = await buildEnhancementsMutation.mutateAsync({
            formData,
            onUploadProgress: (e) => trackUploadProgress(fileItem, e),
          });

          // Store enhancement data
          setVideosToEnhance((prev) => [
            ...prev,
            {
              media: {
                identityId: fileItem.identityId,
                title: fileItem.title,
                formIndex: fileItem.formIndex,
              },
              originalFile: fileItem.file,
              video_preview_url: response.data.video_preview_url,
              thumbnail_suggestions: response.data.thumbnail_suggestions,
              subtitles: response.data.subtitles,
            },
          ]);
        } else {
          // Call standard upload API
          const response = await uploadMutation.mutateAsync({
            formData,
            onUploadProgress: (e) => trackUploadProgress(fileItem, e),
          });

          // Add to Redux state
          dispatch(addMedia(response.data));
        }

        // Clear progress interval
        if (progressIntervalsRef.current[fileItem.formIndex]) {
          clearInterval(progressIntervalsRef.current[fileItem.formIndex]);
          delete progressIntervalsRef.current[fileItem.formIndex];
        }

        // Update to success
        setFiles((prev) =>
          prev.map((f) =>
            f.formIndex === fileItem.formIndex
              ? { ...f, uploadStatus: 'success', uploadProgress: 100 }
              : f
          )
        );

        return shouldEnhance || false;
      } catch (error) {
        console.error('Upload failed for', fileItem.title, error);

        // Clear progress interval
        if (progressIntervalsRef.current[fileItem.formIndex]) {
          clearInterval(progressIntervalsRef.current[fileItem.formIndex]);
          delete progressIntervalsRef.current[fileItem.formIndex];
        }

        // Mark as error
        setFiles((prev) =>
          prev.map((f) =>
            f.formIndex === fileItem.formIndex
              ? { ...f, uploadStatus: 'error', uploadProgress: 0 }
              : f
          )
        );

        throw error;
      }
    },
    [accountId, uploadMutation, buildEnhancementsMutation, dispatch, trackUploadProgress]
  );

  const handleUpload = useCallback(async () => {
    if (files.length === 0 || isUploading) {
      return;
    }

    // Validate identity is selected
    if (!selectedIdentityId) {
      showError('Please select an identity');
      return;
    }

    setIsUploading(true);
    const errors: string[] = [];

    try {
      // Upload all files
      await Promise.allSettled(
        files.map(async (fileItem) => {
          try {
            await uploadSingleFile(fileItem);
            return fileItem;
          } catch (error) {
            errors.push(fileItem.title);
            return null;
          }
        })
      );

      // Wait a bit for state to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Count successes (non-enhancement uploads)
      const successCount = files.filter(
        (f) => f.uploadStatus === 'success' && !f.wantsEnhancement
      ).length;

      // Update total count in Redux
      if (successCount > 0) {
        dispatch(setTotalCount(currentTotalCount + successCount));
        showSuccess(
          `Successfully uploaded ${successCount} file${successCount > 1 ? 's' : ''}`
        );
      }

      if (errors.length > 0) {
        showError(
          `Failed to upload ${errors.length} file${errors.length > 1 ? 's' : ''}: ${errors.join(', ')}`
        );
      }
    } catch (error) {
      console.error('Upload process failed:', error);
      showError('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    files,
    isUploading,
    selectedIdentityId,
    uploadSingleFile,
    showError,
    showSuccess,
    onClose,
    currentTotalCount,
    dispatch,
  ]);

  const handleNextVideoOrFinish = useCallback(async () => {
    if (enhancementInProgress) return;

    const currentVideo = videosToEnhance[currentVideoIndex];
    if (!currentVideo) return;

    setEnhancementInProgress(true);
    setEnhancementProgress(0);

    try {
      // Create FormData
      const formData = new FormData() as UploadMediaFormData;
      formData.append('account_id', accountId);
      formData.append('identity_id', currentVideo.media.identityId);
      formData.append('title', currentVideo.media.title);
      formData.append('initial_file_upload', currentVideo.originalFile);

      // Add enhancement fields
      if (selectedThumbnail) {
        formData.append('video_poster_url', selectedThumbnail.url);
      }
      if (subtitlesEnabled && captions.length > 0) {
        formData.append('video_subtitles', JSON.stringify(captions));
      }

      const uploadStartTime = Date.now();
      let uploadTotalTimeMs = 0;
      let progressInterval: NodeJS.Timeout | null = null;

      // Upload with progress tracking
      const response = await uploadMutation.mutateAsync({
        formData,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          if (!total) return;

          const realProgress = (loaded / total) * 50;
          setEnhancementProgress(Math.min(realProgress, 50));

          // When upload reaches 50%, start estimated progress
          if (realProgress >= 49) {
            uploadTotalTimeMs = Date.now() - uploadStartTime;

            if (progressInterval) {
              clearInterval(progressInterval);
            }

            progressInterval = setInterval(() => {
              const elapsed = Date.now() - uploadStartTime;
              const estimatedProgress =
                50 +
                ((elapsed - uploadTotalTimeMs) / (uploadTotalTimeMs * 2 + 135000)) * 50;
              setEnhancementProgress(Math.min(estimatedProgress, 99));
            }, 1000);
          }
        },
      });

      if (progressInterval) {
        clearInterval(progressInterval);
      }

      setEnhancementProgress(100);

      // Add to Redux state
      dispatch(addMedia(response.data));
      dispatch(setTotalCount(currentTotalCount + 1));

      // Check if there are more videos
      if (currentVideoIndex < videosToEnhance.length - 1) {
        // Move to next video
        const nextIndex = currentVideoIndex + 1;
        setCurrentVideoIndex(nextIndex);
        setSelectedThumbnail(null);
        setNoThumbnailSelected(true);
        setActiveTab('thumbnails');

        const nextVideo = videosToEnhance[nextIndex];
        setHasSubtitles(!!nextVideo.subtitles && nextVideo.subtitles.length > 0);
        setCaptions(nextVideo.subtitles || []);
        setSubtitlesEnabled(false);
        setEnhancementProgress(0);
      } else {
        // All done - close modal
        showSuccess('Video enhancement completed successfully');
        onClose();
      }
    } catch (error) {
      console.error('Enhancement upload failed:', error);
      showError('Failed to upload enhanced video. Please try again.');
    } finally {
      setEnhancementInProgress(false);
    }
  }, [
    currentVideoIndex,
    videosToEnhance,
    selectedThumbnail,
    subtitlesEnabled,
    captions,
    enhancementInProgress,
    accountId,
    uploadMutation,
    dispatch,
    currentTotalCount,
    showSuccess,
    showError,
    onClose,
  ]);

  const handleThumbnailSelect = useCallback((thumbnail: Thumbnail) => {
    setSelectedThumbnail(thumbnail);
    setNoThumbnailSelected(false);
  }, []);

  const handleDefaultThumbnail = useCallback(() => {
    setSelectedThumbnail(null);
    setNoThumbnailSelected(true);
  }, []);

  const handleCaptionChange = useCallback((index: number, text: string) => {
    setCaptions((prev) =>
      prev.map((caption) => (caption.index === index ? { ...caption, text } : caption))
    );
  }, []);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Computed values
  const isFormValid = files.length > 0 && !isUploading && !!selectedIdentityId;
  const canProceedToNext =
    !enhancementInProgress && (selectedThumbnail !== null || noThumbnailSelected);
  const currentVideo = videosToEnhance[currentVideoIndex];
  const isLastVideo = currentVideoIndex === videosToEnhance.length - 1;
  const modalVariant = 'default';

  // Tab configuration for enhancement step
  const enhancementTabs: TabItem[] = [
    { id: 'thumbnails', label: 'Thumbnails' },
    { id: 'captions', label: 'Captions' },
  ];

  if (!isModalOpen) return null;

  return (
    <div className={classNames('relative', className)}>
      <input
        type="checkbox"
        id={modalName}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />

      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={modalName}
        variant={modalVariant}
        onClose={onClose}>
        <div className="flex h-full flex-col">
          {/* Header */}
          <ModalHeader
            modalName={modalName}
            closeAction="state"
            icon={<UploadIconSvg className="h-5 w-5" />}
            title={step === 'upload' ? 'Upload Media' : 'Enhance Video'}
          />

          {/* Body */}
          <ModalBody className="flex-1 px-5 !py-0">
            {step === 'upload' && (
              <>
                {/* Spacing for consistency */}
                <div className="h-6" />

                {/* Form */}
                <div className="flex flex-col gap-4 pb-4">
                  {/* Identity Selection */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Identity</span>
                    </label>
                    <IdentitySelector
                      className="w-full"
                      identities={availableIdentities}
                      value={selectedIdentityId}
                      onChange={(e) => setSelectedIdentityId(e.target.value)}
                      disabled={isUploading}
                      name="identity_id"
                      placeholder="Select an identity"
                      currentAccountId={accountId} // Using accountId param if available, though typically currentAccountId from redux is safer.
                      // Wait, params.accountId might be what we want if opened with specific context.
                      // actually the component logic for availableIdentities uses params.identities or selectAccountIdentities.
                    />
                  </div>

                  {/* File Drop Zone */}
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Select Files</span>
                    </label>

                    <div
                      className={classNames(
                        'relative flex min-h-[200px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors',
                        {
                          'border-primary bg-primary/5': isDragOver,
                          'dark:border-base-content/20 dark:hover:border-base-content/40 border-gray-300 hover:border-gray-400':
                            !isDragOver,
                        }
                      )}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      onClick={() => document.getElementById('file-input')?.click()}>
                      <UploadIconSvg className="mb-4 h-12 w-12 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and
                        drop
                      </p>
                      <p className="text-xs text-gray-500">
                        Images: PNG, JPG, GIF, TIFF (max 20MB) • Videos: MP4, MOV (max
                        600MB) • vCards: VCF (max 745KB)
                      </p>

                      <input
                        id="file-input"
                        type="file"
                        multiple
                        accept="image/*,video/*,.vcf"
                        onChange={handleInputChange}
                        className="absolute inset-0 cursor-pointer opacity-0"
                      />
                    </div>
                  </div>

                  {/* Selected Files Table */}
                  {files.length > 0 && (
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text font-medium">
                          Selected Files ({files.length})
                        </span>
                      </label>
                      <div className="dark:border-base-content/10 overflow-x-auto rounded-lg border border-gray-200">
                        <table className="table-sm table">
                          <thead>
                            <tr>
                              <th className="w-16">Type</th>
                              <th>Title</th>
                              <th className="w-32 text-center">Enhancement</th>
                              <th className="w-40 text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {files.map((fileItem, index) => {
                              const isVideo = isVideoFile(fileItem.file);
                              const fileName = fileItem.file.name.toLowerCase();
                              const isVCard = fileName.endsWith('.vcf');
                              return (
                                <tr key={`${fileItem.formIndex}`}>
                                  <td>
                                    <div className="flex items-center justify-center">
                                      {isVideo ? (
                                        <VideoSvg className="h-5 w-5 text-gray-600" />
                                      ) : isVCard ? (
                                        <UserTypeIconSvg className="h-5 w-5 text-gray-600" />
                                      ) : (
                                        <ImageSvg className="h-5 w-5 text-gray-600" />
                                      )}
                                    </div>
                                  </td>
                                  <td>
                                    <div className="truncate text-sm font-medium">
                                      {fileItem.title}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {(fileItem.file.size / (1024 * 1024)).toFixed(2)} MB
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    {isVideo ? (
                                      <div className="flex justify-center">
                                        <input
                                          type="checkbox"
                                          className="checkbox checkbox-sm"
                                          checked={fileItem.wantsEnhancement || false}
                                          onChange={(e) =>
                                            handleEnhancementCheckChange(
                                              index,
                                              e.target.checked
                                            )
                                          }
                                          disabled={isUploading}
                                        />
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-400">N/A</span>
                                    )}
                                  </td>
                                  <td className="text-center">
                                    <div className="flex items-center justify-center">
                                      {fileItem.uploadStatus === 'idle' &&
                                        !isUploading && (
                                          <button
                                            type="button"
                                            onClick={() => handleFileRemove(index)}
                                            className="btn btn-ghost btn-xs">
                                            <TrashCanSvg className="h-4 w-4" />
                                          </button>
                                        )}
                                      {fileItem.uploadStatus === 'uploading' && (
                                        <div className="flex items-center gap-2">
                                          <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200">
                                            <div
                                              className="h-full bg-blue-600 transition-all"
                                              style={{
                                                width: `${fileItem.uploadProgress}%`,
                                              }}
                                            />
                                          </div>
                                          <span className="text-xs text-gray-500">
                                            {Math.round(fileItem.uploadProgress)}%
                                          </span>
                                        </div>
                                      )}
                                      {fileItem.uploadStatus === 'success' && (
                                        <CircleCheckSvg className="h-5 w-5 text-green-600" />
                                      )}
                                      {fileItem.uploadStatus === 'error' && (
                                        <CircleTimesSvg className="h-5 w-5 text-red-600" />
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {step === 'enhance' && currentVideo && (
              <div className="flex h-full gap-6 py-6">
                {/* Left Column - Video Player */}
                <div className="flex-[2]">
                  <video
                    key={currentVideo.video_preview_url}
                    src={currentVideo.video_preview_url}
                    poster={selectedThumbnail?.url || ''}
                    controls
                    className="w-full rounded-lg bg-black"
                  />
                  <div className="mt-4 text-sm text-gray-600">
                    <p className="font-medium">
                      Video {currentVideoIndex + 1} of {videosToEnhance.length}:{' '}
                      {currentVideo.media.title}
                    </p>
                  </div>
                </div>

                {/* Right Column - Tabs */}
                <div className="flex flex-1 flex-col">
                  <DetailsTabs
                    tabs={enhancementTabs}
                    activeTab={activeTab}
                    onTabChange={(tabId) =>
                      setActiveTab(tabId as 'thumbnails' | 'captions')
                    }
                  />

                  <div className="flex-1 overflow-y-auto p-4">
                    {activeTab === 'thumbnails' && (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          {currentVideo.thumbnail_suggestions.map((thumbnail, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handleThumbnailSelect(thumbnail)}
                              disabled={enhancementInProgress}
                              className={classNames(
                                'relative overflow-hidden rounded-lg border-2 transition-all hover:scale-105',
                                {
                                  'border-primary ring-primary ring-2':
                                    selectedThumbnail?.url === thumbnail.url,
                                  'border-gray-300':
                                    selectedThumbnail?.url !== thumbnail.url,
                                }
                              )}>
                              {/* eslint-disable-next-line @next/next/no-img-element */}
                              <img
                                src={thumbnail.url}
                                alt={`Thumbnail ${index + 1}`}
                                className="aspect-video h-auto w-full object-cover"
                              />
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={handleDefaultThumbnail}
                          disabled={enhancementInProgress}
                          className={classNames('btn btn-outline btn-sm w-full', {
                            'btn-primary': noThumbnailSelected,
                          })}>
                          Use Default Thumbnail
                        </button>
                      </div>
                    )}

                    {activeTab === 'captions' && (
                      <div className="space-y-4">
                        {hasSubtitles ? (
                          <>
                            <div className="form-control">
                              <label className="label cursor-pointer justify-start gap-3">
                                <input
                                  type="checkbox"
                                  className="checkbox"
                                  checked={subtitlesEnabled}
                                  onChange={(e) => setSubtitlesEnabled(e.target.checked)}
                                  disabled={enhancementInProgress}
                                />
                                <span className="label-text">Enable Captions</span>
                              </label>
                            </div>

                            {subtitlesEnabled && (
                              <>
                                <div className="alert alert-warning alert-soft">
                                  <CircleExclamationOutlineSvg className="h-5 w-5" />
                                  <span className="text-sm">
                                    Captions won&apos;t be visible in the preview. Please
                                    review the captions and make sure they are correct.
                                  </span>
                                </div>

                                <div className="space-y-3">
                                  {captions.map((caption) => (
                                    <div key={caption.index} className="space-y-2">
                                      <div className="text-sm text-gray-500">
                                        {formatCaptionTime(caption.start)} →{' '}
                                        {formatCaptionTime(caption.end)}
                                      </div>
                                      <textarea
                                        className="textarea textarea-bordered w-full"
                                        rows={2}
                                        value={caption.text}
                                        onChange={(e) =>
                                          handleCaptionChange(
                                            caption.index,
                                            e.target.value
                                          )
                                        }
                                        disabled={enhancementInProgress}
                                        placeholder="Caption text"
                                      />
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </>
                        ) : (
                          <InfoAlert>
                            No captions were provided. If you think this is in error,
                            please try to upload the video again. If problem persists,
                            please contact support.
                          </InfoAlert>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </ModalBody>

          {/* Footer */}
          {step === 'upload' && (
            <ModalFooter>
              <div className="flex w-full justify-end gap-3">
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleCancel}
                  disabled={isUploading}>
                  Cancel
                </button>

                {!isUploading &&
                  videosToEnhance.length > 1 &&
                  files.every(
                    (f) => f.uploadStatus === 'success' || f.uploadStatus === 'error'
                  ) && (
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={startEnhancement}>
                      Enhance Videos ({videosToEnhance.length})
                    </button>
                  )}

                {(files.some((f) => f.uploadStatus === 'idle') ||
                  !files.every(
                    (f) => f.uploadStatus === 'success' || f.uploadStatus === 'error'
                  )) && (
                  <button
                    type="button"
                    className={classNames('btn btn-primary', {
                      'btn-disabled': !isFormValid,
                      loading: isUploading,
                    })}
                    onClick={handleUpload}
                    disabled={!isFormValid}>
                    {isUploading
                      ? 'Uploading...'
                      : `Upload ${files.length} file${files.length > 1 ? 's' : ''}`}
                  </button>
                )}
              </div>
            </ModalFooter>
          )}

          {step === 'enhance' && (
            <ModalFooter
              progressPercentage={
                enhancementInProgress ? enhancementProgress : undefined
              }>
              <div className="flex w-full justify-end gap-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleNextVideoOrFinish}
                  disabled={!canProceedToNext}>
                  {enhancementInProgress
                    ? 'Processing...'
                    : isLastVideo
                      ? 'Finish'
                      : 'Next Video'}
                </button>
              </div>
            </ModalFooter>
          )}
        </div>
      </ModalContainer>
    </div>
  );
};

export default MediaUploadModal;
